import React, { Fragment } from 'react';
import { formatDatetime, formatEmail, formatMawb, formatDecimal } from '../../../utils';
import _ from 'lodash';
import { ExportDimension, IExport, IQueue, IUser } from '../../../globals/interfaces';
import dayjs from 'dayjs';
import { DimTotal } from './ModalExportDimensions';

const CreateExportDeliverySheet = ({
    user,
    stationInfo,
    queueRecord,
    exportRecord,
    dimensions,
    totals
}: {
    user: IUser;
    stationInfo: any;
    queueRecord: IQueue;
    exportRecord: IExport;
    dimensions: Array<ExportDimension>;
    totals: Array<DimTotal>;
}) => {
    // Function to chunk the images array into groups of 3 for grid display
    const chunkImages = (images) => {
        if (!images || !Array.isArray(images)) return [];
        return _.chunk(images, 3);
    };

    // Create image grid HTML
    const createImageGrid = (dimensions: Array<ExportDimension>) => {
        let imageGridHTML = '';
        
        // Loop through dimensions and process images if they exist
        dimensions.forEach((dimension, dimensionIndex) => {
            if (dimension.images && Array.isArray(dimension.images) && dimension.images.length > 0) {
                const { images, length, width, height, weight, created, createdBy } = dimension;
                const imageRows = images.map(img => [img]);
                const dimsSummary = `${length}x${width}x${height} ${weight} kg. Created by ${formatEmail(createdBy)} at ${formatDatetime(created)}`;
                
                // Start dimension images section with header
                imageGridHTML += `
                    <div class="dimension-images">
                        <h3>Dimension #${dimensionIndex + 1}: ${dimsSummary}</h3>
                        <div class="image-container">`;
                
                // Process each row of images (3 per row)
                imageRows.forEach((row) => {
                    imageGridHTML += '<div class="image-row">';
                    
                    // Process each image in the row
                    row.forEach((image) => {
                        imageGridHTML += `
                        <div class="image-cell">
                            <img src="${image.accessLink}" alt="Dimension Image" class="dimension-image">
                        </div>`;
                    });
                    
                    imageGridHTML += '</div>';
                });
                
                imageGridHTML += `
                    </div>
                </div>`;
            }
        });
        
        return imageGridHTML;
    };

    return (
        <div dangerouslySetInnerHTML={{
            __html: `
            <!DOCTYPE html>
            <html lang="en">

                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>Export Record</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 0;
                            padding: 20px;
                            box-sizing: border-box;
                            width: 210mm;
                            /* A4 width */
                            min-height: 297mm;
                            /* A4 height */
                        }

                        /* Print-specific styles */
                        @media print {
                            * {
                                -webkit-print-color-adjust: exact !important;
                                /* Chrome, Safari */
                                color-adjust: exact !important;
                                /* Firefox */
                                print-color-adjust: exact !important;
                                /* Future standard */
                            }

                            body {
                                margin: 0;
                                padding: 0;
                            }

                            @page {
                                size: A4;
                                margin: 10mm 10mm 10mm 10mm; /* Top, Right, Bottom, Left */
                            }
                        }

                        .container {
                            width: 100%;
                            max-width: 800px;
                            margin: 0 auto;
                        }

                        .header-row {
                            display: flex;
                            flex-wrap: wrap;
                            margin-bottom: 20px;
                        }

                        .header-col {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            margin-bottom: 10px;
                        }

                        .header-col:first-child {
                            flex: 0 0 30%;
                            align-items: flex-start;
                            /* padding-left: 40px; */
                        }

                        .header-col:not(:first-child) {
                            flex: 0 0 15%;
                        }

                        .header-col img {
                            margin-bottom: 5px;
                        }

                        .header-col h6 {
                            margin: 0;
                            text-align: center;
                            font-size: 12px;
                        }

                        .header {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-bottom: 10px;
                        }

                        .title {
                            font-size: 24px;
                            font-weight: bold;
                        }

                        .record-number {
                            background-color: #4a90e2;
                            color: white;
                            padding: 5px 15px;
                            font-size: 32px;
                            font-weight: bold;
                        }

                        .table-container {
                            display: flex;
                            gap: 10px;
                            margin-bottom: 15px;
                        }

                        .table-half {
                            flex: 1;
                        }

                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 15px;
                        }

                        table.info-table th {
                            background-color: #4a90e2;
                            color: white;
                            text-align: center;
                            padding: 5px;
                            font-weight: bold;
                        }

                        table.info-table td {
                            border: 1px solid #ddd;
                            padding: 5px;
                        }

                        table.info-table td:first-child {
                            font-weight: bold;
                            width: 40%;
                        }

                        table.info-table tr:nth-child(odd) {
                            background-color: #f2f2f2;
                        }

                        table.info-table tr:nth-child(even) {
                            background-color: #e1dddc;
                        }

                        table.data-table th {
                            background-color: #4a90e2;
                            color: white;
                            text-align: center;
                            padding: 5px;
                        }

                        table.data-table td {
                            border: 1px solid #ddd;
                            padding: 5px;
                            text-align: center;
                        }

                        table.data-table tr:nth-child(odd) {
                            background-color: #f2f2f2;
                        }

                        table.data-table tr:nth-child(even) {
                            background-color: #e1dddc;
                        }

                        .receipt {
                            margin-top: 20px;
                            font-size: 0.9em;
                        }
                        
                        /* Image grid styling */
                        .dimension-images {
                            margin-top: 30px;
                            page-break-inside: avoid;
                        }
                        
                        .dimension-images h3 {
                            background-color: #4a90e2;
                            color: white;
                            padding: 5px 10px;
                            margin-bottom: 10px;
                        }
                        
                        .image-container {
                            width: 100%;
                        }
                        
                        .image-row {
                            display: block; /* stack vertically */
                            margin-bottom: 15px;
                        }
                        
                        .image-cell {
                            width: 100%; /* full width */
                            padding: 0; /* remove padding if needed */
                            border: none; /* optional: remove border if not needed */
                        }
                        
                        .dimension-image {
                            // width: 100%;
                            height: 30%;
                            width: auto;
                            display: block;
                        }
                        
                        /* Footer styling */
                        .page-footer {
                            text-align: center;
                            font-weight: bold;
                            padding: 10px;
                            position: fixed;
                            bottom: 0;
                            left: 0;
                            width: 100%;
                            background-color: white;
                            border-top: 1px solid #ddd;
                        }
                        

                        .logo {
                            width: 15px;
                        }

                        /* Add page break after receipt statement */
                        .page-break-after {
                            page-break-after: always;
                        }
                        
                        /* Add bottom padding to ensure content doesn't overlap with fixed footer */
                        .container {
                            width: 100%;
                            max-width: 800px;
                            margin: 0 auto;
                            padding-bottom: 50px; /* Space for footer */
                        }

                        
                        .totals-container {
                            display: flex;
                            gap: 10px;
                            margin-bottom: 10px;
                        }

                        .total-box {
                            height: 100px;
                            width: 200px;
                            background-color: #4a90e2;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            color: white;
                            border-radius: 20px;
                            h5 {
                                margin: 2px;
                            }
                        }
                    </style>
                </head>

                <body>
                    <div class="container">
                        <!-- Header with logo and contact info -->
                        <div class="header-row">
                            <div class="header-col">
                                <img src="https://ewrstorage1.blob.core.windows.net/pics/choice.pod.logo.01.png"
                                    style="width: 200px; height: auto;">
                            </div>
                            <div class="header-col">
                                <img src="https://ewrstorage1.blob.core.windows.net/pics/choice.pod.add.01.png"
                                    style="width: 75px; height: auto;">
                                <h6>${stationInfo.s_address}</h6>
                            </div>
                            <div class="header-col">
                                <img src="https://ewrstorage1.blob.core.windows.net/pics/choice.pod.phone.01.png"
                                    style="width: 75px; height: auto;">
                                <h6>${stationInfo.s_phone}</h6>
                            </div>
                            <div class="header-col">
                                <img src="https://ewrstorage1.blob.core.windows.net/pics/choice.pod.email.01.png"
                                    style="width: 75px; height: auto;">
                                <h6>${stationInfo.email || ''}</h6>
                            </div>
                            <div class="header-col">
                                <img src="https://ewrstorage1.blob.core.windows.net/pics/choice.pod.web.01.png"
                                    style="width: 75px; height: auto;">
                                <h6>www.choice.aero</h6>
                            </div>
                        </div>

                        <div class="header">
                            <div class="title">Export Record</div>
                            <div class="record-number">${formatMawb(queueRecord.s_mawb)}</div>
                        </div>

                        <div class="table-container">
                            <!-- AWB Table -->
                            <div class="table-half">
                                <table class="info-table">
                                    <thead>
                                        <tr>
                                            <th colspan="2">For AWB ${formatMawb(queueRecord.s_mawb)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Pieces</td>
                                            <td>${exportRecord.i_pieces}</td>
                                        </tr>
                                        <tr>
                                            <td>Weight</td>
                                            <td>${exportRecord.i_weight}</td>
                                        </tr>
                                        <tr>
                                            <td>Commodity</td>
                                            <td>${exportRecord.s_commodity}</td>
                                        </tr>
                                        <tr>
                                            <td>Origin</td>
                                            <td>${exportRecord.s_origin}</td>
                                        </tr>
                                        <tr>
                                            <td>Destination</td>
                                            <td>${exportRecord.s_destination}</td>
                                        </tr>
                                        <tr>
                                            <td>Accepted from</td>
                                            <td>${queueRecord.s_trucking_driver}</td>
                                        </tr>
                                        <tr>
                                            <td>Company</td>
                                            <td>${queueRecord.s_trucking_company}</td>
                                        </tr>
                                        <tr>
                                            <td>Phone</td>
                                            <td>${queueRecord.s_trucking_cell}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Process Detail Table -->
                            <div class="table-half">
                                <table class="info-table">
                                    <thead>
                                        <tr>
                                            <th colspan="2">Process Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Check-in Time</td>
                                            <td>${formatDatetime(queueRecord.t_kiosk_submitted)}</td>
                                        </tr>
                                        <tr>
                                            <td>Office Start Time</td>
                                            <td>${formatDatetime(queueRecord.t_counter_start)}</td>
                                        </tr>
                                        <tr>
                                            <td>Office Process Agent</td>
                                            <td>${formatEmail(queueRecord.s_counter_ownership_agent)}</td>
                                        </tr>
                                        <tr>
                                            <td>Dock Start Time</td>
                                            <td>${formatDatetime(queueRecord.t_dock_ownership)}</td>
                                        </tr>
                                        <tr>
                                            <td>Dock Agent</td>
                                            <td>${formatEmail(queueRecord.s_dock_ownership)}</td>
                                        </tr>
                                        <tr>
                                            <td>Dock Complete Time</td>
                                            <td>${formatDatetime(queueRecord.t_dock_complete)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div class="totals-container">
                            ${
                                totals.map((total) => `
                                    <div class="total-box">
                                        <h5>${total.value}</h5>
                                        <h5>${total.name}</h5>
                                    </div>
                                `)
                            }
                        </div>

                        <table class="data-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Pieces</th>
                                    <th>Length</th>
                                    <th>Width</th>
                                    <th>Height</th>
                                    <th>Volume</th>
                                    <th>Weight</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${
                                    dimensions.map((item, i) => (
                                        `<tr>
                                            <td>${i + 1}</td>
                                            <td>${item.pieces}</td>
                                            <td>${formatDecimal(item.length)}</td>
                                            <td>${formatDecimal(item.width)}</td>
                                            <td>${formatDecimal(item.height)}</td>
                                            <td>${formatDecimal(item.volume) || ''}</td>
                                            <td>${formatDecimal(item.weight)}</td>
                                            <td>${formatDatetime(item.created)}</td>
                                        </tr>`
                                    )).join('')
                                }
                            </tbody>
                        </table>

                        <div class="receipt">
                            Receipt Statement: Requested by ${formatEmail(user.s_email)} on ${dayjs().format('MM/DD/YYYY HH:mm')}
                        </div>
                        
                        <!-- Force page break after receipt statement -->
                        <div class="page-break-after"></div>
                        
                        <!-- Image Grid Section -->
                        ${createImageGrid(dimensions)}
                        
                        <!-- Fixed footer on every page -->
                        <div class="page-footer">EOS <img class="logo" src="https://ewrstorage1.blob.core.windows.net/pics/eos-logo-small-alt.png" /></div>
                    </div>
                    
                    <script>
                        // Optional: Auto-print after loading
                        setTimeout(() => {
                            window.print();
                        }, 1000);
                    </script>
                </body>
            </html>
            ` }}>
        </div>
    );
}

export default CreateExportDeliverySheet;